// Reports tab — FE-SCRM-058.
//
// Page-level period selector (month / quarter / year / all) cascades
// to every period-aware tile. Each card mounts its own hook so a slow
// query on one tile doesn't block the others.

import { useState } from "react";

import { StakeholderRisksTile } from "@/features/sales/StakeholderMap";

import { CompetitorIntelligenceTile } from "./CompetitorIntelligenceTile";
import { LossAnalysisTile } from "./LossAnalysisTile";
import { PERIOD_OPTIONS } from "./labels";
import { PipelineVelocityTile } from "./PipelineVelocityTile";
import { ReferralGraphTile } from "./ReferralGraphTile";
import type { ReportPeriod } from "./types";


export function ReportsPanel() {
  const [period, setPeriod] = useState<ReportPeriod>("quarter");

  return (
    <div className="flex flex-col gap-4">
      <PeriodSelector value={period} onChange={setPeriod} />
      {/* Top row is asymmetric: the stakeholder-risk list is a narrow column
          of cards, while pipeline velocity carries several data tables and
          needs the room. 1/3 : 2/3 at xl so velocity isn't crowded. */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <StakeholderRisksTile />
        <div className="xl:col-span-2">
          <PipelineVelocityTile />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ReferralGraphTile period={period} />
        <LossAnalysisTile period={period} />
        <div className="xl:col-span-2">
          <CompetitorIntelligenceTile period={period} />
        </div>
      </div>
    </div>
  );
}


function PeriodSelector({
  value, onChange,
}: { value: ReportPeriod; onChange: (next: ReportPeriod) => void }) {
  return (
    <div
      data-testid="reports-period-selector"
      role="tablist"
      aria-label="Report period"
      className="flex items-center gap-2"
    >
      <span className="text-xs text-text-tertiary">Period:</span>
      <div className="inline-flex w-fit rounded-md border border-border-subtle bg-recessed p-1">
        {PERIOD_OPTIONS.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`reports-period-${opt.value}`}
              onClick={() => onChange(opt.value)}
              className={[
                "rounded-sm px-2.5 py-1 text-xs font-medium transition",
                active
                  ? "bg-canvas text-text-primary shadow-sm"
                  : "text-text-secondary hover:text-text-primary",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
